/**
 * Entity base: anything that moves and is not the player or a placed object.
 *
 * Enemies, pickups, projectiles, platforms and particles all share this
 * interface. Per-instance mutable state goes in `data` (a plain object) rather
 * than being baked into the class, which keeps the entity model data-driven and
 * means a level file can carry per-placement configuration.
 */

import type { LevelRuntime, PlayerLike } from '../game/types'
import { TILE } from './constants'
import { makeBody, stepBody, type Body, type SolidQuery } from './collision'

export interface Entity {
  /** Unique per level instance. */
  uid: number
  /** Registered entity kind. */
  kind: string
  x: number
  y: number
  w: number
  h: number
  vx: number
  vy: number
  facing: 1 | -1
  onGround: boolean
  /** False once killed/collected; the level drops it at end of step. */
  alive: boolean
  /** Free per-instance state. */
  data: Record<string, unknown>
  /** Physics body view, for helpers that need it. */
  body?: Body
  update?(level: LevelRuntime, player: PlayerLike): void
  draw?(ctx: CanvasRenderingContext2D, camX: number, camY: number): void
  /** Collision response when the player stomps it from above. */
  onStomp?(level: LevelRuntime, player: PlayerLike): boolean
  /** Collision response when the player touches it. */
  onTouch?(level: LevelRuntime, player: PlayerLike): void
}

/** Stateful base for a walking/flying enemy or a moving platform. */
export class EntityBase implements Entity {
  uid = 0
  kind = 'entity'
  x = 0
  y = 0
  w = TILE
  h = TILE
  vx = 0
  vy = 0
  facing: 1 | -1 = -1
  onGround = false
  alive = true
  data: Record<string, unknown> = {}
  body?: Body

  constructor(kind: string, x: number, y: number, w: number, h: number) {
    this.kind = kind
    this.x = x
    this.y = y
    this.w = w
    this.h = h
  }

  /**
   * Gravity, ground friction and tile collision, in the usual order.
   *
   * Gravity is applied to the *body* copy before integration and read back
   * afterwards. An earlier version added gravity to `this.vy` and then
   * overwrote `this.vy` with the body's value, discarding it every frame — so
   * anything stepped through here floated. The agent that wrote the entities
   * worked around it by pre-applying gravity and passing 0; with this fixed
   * that workaround is harmless (it would just apply gravity twice, so I have
   * left their callers alone and verified the net result below).
   */
  physicsStep(world: SolidQuery, gravity: number, friction: number): void {
    const b =
      this.body ?? (this.body = makeBody(this.x, this.y, this.w, this.h))
    b.x = this.x
    b.y = this.y
    b.w = this.w
    b.h = this.h
    b.vx = this.vx
    // Carry last frame's *post-collision* velocity forward, then add gravity.
    b.vy = this.vy + gravity
    b.ignoreOneWay = false
    b.onGround = false
    stepBody(b, world)
    this.x = b.x
    this.y = b.y
    this.vx = b.vx
    this.vy = b.vy
    this.onGround = b.onGround
    if (friction > 0 && this.onGround) {
      if (Math.abs(this.vx) <= friction) this.vx = 0
      else this.vx -= Math.sign(this.vx) * friction
    }
  }

  /** True if there is a wall ahead at the given lookahead. */
  wallAhead(world: SolidQuery, look = 2): boolean {
    const tx = Math.floor((this.facing > 0 ? this.x + this.w : this.x) / TILE)
    const ty0 = Math.floor((this.y + 1) / TILE)
    const ty1 = Math.floor((this.y + this.h - 1) / TILE)
    for (let ty = ty0; ty <= ty1; ty++) {
      if (world.isSolid(tx, ty)) return true
      for (let d = 1; d <= look; d++) {
        if (world.isSolid(tx + this.facing * d, ty)) return true
      }
    }
    return false
  }

  /** True if there is ground ahead; used for ledge-aware AI. */
  groundAhead(world: SolidQuery, look = 2): boolean {
    const fx = this.facing > 0 ? this.x + this.w + 1 : this.x - 1
    const tx = Math.floor(fx / TILE)
    const ty = Math.floor((this.y + this.h + 1) / TILE)
    if (world.isSolid(tx, ty) || world.isOneWay(tx, ty)) return true
    for (let d = 1; d <= look; d++) {
      if (world.isSolid(tx + this.facing * d, ty)) return true
    }
    return false
  }

  update(_level: LevelRuntime, _player: PlayerLike): void {
    /* subclasses override */
  }
}
